import { ReceivableRow } from '@bariendo/lib/receivables';
import { Box, SegmentedControl, UnstyledButton } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { HubTable } from '@/components/Hub/HubTable';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { NotePopoverCell } from '@/components/Hub/NotePopoverCell';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { Count, QueueFooter } from '@/components/Hub/QueueFooter';
import { useNow } from '@/hooks/useNow';
import { CohortTruncationNotice } from '@/pages/PatientListPage/components/CohortTruncationNotice';
import { alert } from '@/services/alert';
import { downloadAttachmentBlob } from '@/services/attachments';
import { ORBIT_DISABLED, RECEIVABLE_KIND_TONES } from '@/theme/colors';
import { LedgerCell, LedgerEmptyCell } from '../ledger/LedgerCell';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { Amount, Head, SummaryTable, SummaryTables, Who } from '../ledger/SummaryCells';
import { EditReceivableCell } from './EditReceivableCell';
import { FeeScheduleView } from './FeeScheduleView';
import {
  BILL_TO_LABEL,
  BILL_TOS,
  DEFAULT_RECEIVABLE_FILTERS,
  filterReceivables,
  formatReceivableDate,
  invoicedAmount,
  isFiltered,
  ReceivableFiltersState,
  receivablesCsv,
  receivablesCsvFilename,
  summarizeReceivables,
} from './receivableRows';
import { ReceivableStatusCell } from './ReceivableStatusCell';
import { ReceivableActions, useReceivableActions } from './useReceivableActions';
import { ReceivablesState } from './useReceivables';

/** Finance ops → Receivables (BAR-3038), as the prototype draws it. */

const VIEW_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

const BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
  whiteSpace: 'nowrap',
};

const BADGE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 500,
  padding: '1px 9px',
  borderRadius: 100,
  whiteSpace: 'nowrap',
};

const MONEY_STYLE: CSSProperties = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
const RIGHT = { align: 'right' as const };

const TRACK_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'employer', label: 'Employer' },
  { value: 'insurance', label: 'Insurance' },
];
const BILL_TO_OPTIONS = [
  { value: 'all', label: 'All' },
  ...BILL_TOS.map(({ billTo }) => ({ value: billTo, label: BILL_TO_LABEL[billTo] })),
];
const TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'CONSULTATION_FEE', label: 'Consultation fee' },
];
const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'to_invoice', label: 'To invoice' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'paid', label: 'Paid' },
  { value: 'all', label: 'All' },
];

function dateCell(date: string | null): ReactElement {
  return date ? <LedgerCell>{formatReceivableDate(date)}</LedgerCell> : <LedgerEmptyCell />;
}

function moneyCell(value: number | null): ReactElement {
  return value === null ? (
    <LedgerCell color={ORBIT_DISABLED} style={MONEY_STYLE}>
      —
    </LedgerCell>
  ) : (
    <LedgerCell style={MONEY_STYLE}>{formatLedgerMoney(value)}</LedgerCell>
  );
}

function receivableColumns(actions: ReceivableActions, now: Date): MRT_ColumnDef<ReceivableRow>[] {
  return [
    {
      id: 'patient',
      header: 'Patient',
      size: 120,
      grow: true,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original?.patientName ? (
          <LedgerCell title={row.original.patientName}>{row.original.patientName}</LedgerCell>
        ) : (
          <LedgerEmptyCell />
        ),
    },
    {
      id: 'billTo',
      header: 'Bill to',
      size: 104,
      enableSorting: false,
      Cell: ({ row }) => <LedgerCell>{row.original?.billTo ? BILL_TO_LABEL[row.original.billTo] : ''}</LedgerCell>,
    },
    {
      id: 'type',
      header: 'Type',
      size: 92,
      enableSorting: false,
      Cell: ({ row }) => {
        if (!row.original?.type) return null;
        const fee = row.original.type === 'CONSULTATION_FEE';
        return (
          <span style={{ ...BADGE_STYLE, ...RECEIVABLE_KIND_TONES[fee ? 'consultationFee' : 'procedure'] }}>
            {fee ? 'Consult fee' : 'Procedure'}
          </span>
        );
      },
    },
    {
      id: 'serviceDate',
      header: 'Service date',
      size: 92,
      enableSorting: false,
      Cell: ({ row }) => dateCell(row.original?.serviceDate ?? null),
    },
    {
      id: 'status',
      header: 'Status',
      size: 92,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original?.status ? <ReceivableStatusCell row={row.original} actions={actions} now={now} /> : null,
    },
    {
      id: 'invoiceDate',
      header: 'Invoice date',
      size: 88,
      enableSorting: false,
      Cell: ({ row }) => dateCell(row.original?.invoicedOn ?? null),
    },
    {
      id: 'invoiced',
      header: 'Invoiced',
      size: 84,
      enableSorting: false,
      mantineTableHeadCellProps: RIGHT,
      mantineTableBodyCellProps: RIGHT,
      Cell: ({ row }) => (row.original?.status ? moneyCell(invoicedAmount(row.original)) : null),
    },
    {
      id: 'paidDate',
      header: 'Paid date',
      size: 88,
      enableSorting: false,
      Cell: ({ row }) => dateCell(row.original?.paidOn ?? null),
    },
    {
      id: 'received',
      header: 'Received',
      size: 84,
      enableSorting: false,
      mantineTableHeadCellProps: RIGHT,
      mantineTableBodyCellProps: RIGHT,
      Cell: ({ row }) => moneyCell(row.original?.status === 'PAID' ? row.original.paidAmount : null),
    },
    {
      id: 'bankReference',
      header: 'Bank reference',
      size: 96,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original?.bankReference ? (
          <LedgerCell title={row.original.bankReference}>{row.original.bankReference}</LedgerCell>
        ) : (
          <LedgerEmptyCell />
        ),
    },
    {
      id: 'edit',
      header: '',
      size: 24,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original?.status && row.original.status !== 'TO_INVOICE' ? (
          <EditReceivableCell row={row.original} actions={actions} />
        ) : null,
    },
    {
      id: 'note',
      header: '',
      size: 24,
      enableSorting: false,
      Cell: ({ row }) => {
        const line = row.original;
        return line?.id ? <NotePopoverCell note={line.note} onSave={(note) => actions.saveNote(line, note)} /> : null;
      },
    },
  ];
}

export interface ReceivablesTabProps {
  receivables: ReceivablesState;
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

type ReceivablesView = 'lines' | 'fee-schedule';
const VIEW_OPTIONS: { label: string; value: ReceivablesView }[] = [
  { label: 'Lines', value: 'lines' },
  { label: 'Fee schedule', value: 'fee-schedule' },
];

/** Receivables: its lines, or the fee schedule they're priced from (BAR-3039); the view lives in `?view=`. */
export function ReceivablesTab({ receivables, now }: ReceivablesTabProps): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ReceivablesView = searchParams.get('view') === 'fee-schedule' ? 'fee-schedule' : 'lines';

  return (
    <Box style={VIEW_STYLE}>
      <Box style={{ padding: '12px 16px 0' }}>
        <SegmentedControl
          size="xs"
          data={VIEW_OPTIONS}
          value={view}
          onChange={(value) =>
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('view', value);
              return next;
            })
          }
        />
      </Box>
      <Box mt={12} style={VIEW_STYLE}>
        {view === 'lines' ? <ReceivableLines receivables={receivables} now={now} /> : <FeeScheduleView now={now} />}
      </Box>
    </Box>
  );
}

function ReceivableLines({ receivables, now }: ReceivablesTabProps): ReactElement {
  const resolvedNow = useNow(now);
  const [filters, setFilters] = useState<ReceivableFiltersState>(DEFAULT_RECEIVABLE_FILTERS);
  const actions = useReceivableActions(receivables.reload);
  const columns = useMemo(() => receivableColumns(actions, resolvedNow), [actions, resolvedNow]);

  useEffect(() => {
    if (receivables.error) alert.error(`Couldn't load receivables: ${receivables.error}`);
  }, [receivables.error]);

  // The page reads receivables for the tab's count, so a read that failed before the tab opened
  // would otherwise sit there until a reload (BAR-3125).
  useEffect(() => {
    if (receivables.error) receivables.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on opening
  }, []);

  const visible = useMemo(
    () => filterReceivables(receivables.receivables, filters),
    [receivables.receivables, filters]
  );
  const summary = useMemo(
    () => summarizeReceivables(receivables.receivables, resolvedNow),
    [receivables.receivables, resolvedNow]
  );
  const month = resolvedNow.toLocaleString('en-US', { month: 'short' });
  const set =
    <K extends keyof ReceivableFiltersState>(key: K) =>
    (value: string) =>
      setFilters((current) => ({ ...current, [key]: value as ReceivableFiltersState[K] }));

  const exportCsv = (): void => {
    const url = URL.createObjectURL(new Blob([receivablesCsv(visible)], { type: 'text/csv' }));
    downloadAttachmentBlob(url, receivablesCsvFilename(resolvedNow));
  };

  return (
    <Box style={VIEW_STYLE}>
      <QueueFilterBar
        filters={[
          { label: 'Track', value: filters.track, options: TRACK_OPTIONS, onChange: set('track') },
          { label: 'Bill to', value: filters.billTo, options: BILL_TO_OPTIONS, onChange: set('billTo') },
          { label: 'Type', value: filters.type, options: TYPE_OPTIONS, onChange: set('type') },
          { label: 'Status', value: filters.status, options: STATUS_OPTIONS, onChange: set('status') },
        ]}
        actions={
          <UnstyledButton style={BUTTON_STYLE} onClick={exportCsv}>
            Export CSV
          </UnstyledButton>
        }
        isFiltered={isFiltered(filters)}
        onReset={() => setFilters(DEFAULT_RECEIVABLE_FILTERS)}
      />
      {receivables.hasMore && <CohortTruncationNotice loaded={receivables.receivables.length} />}
      {!receivables.error && (
        <SummaryTables>
          <SummaryTable label="By bill to">
            <thead>
              <tr>
                <Head who>Bill to</Head>
                <Head who>Track</Head>
                <Head>To invoice</Head>
                <Head>Invoiced</Head>
                <Head>{`Paid in ${month}`}</Head>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) =>
                row.billTo === 'total' ? (
                  <tr key="total">
                    <Who total>Total</Who>
                    <Who total>{''}</Who>
                    <Amount value={row.toInvoice} total />
                    <Amount value={row.invoiced} total />
                    <Amount value={row.paidThisMonth} total />
                  </tr>
                ) : (
                  <tr key={row.billTo}>
                    <Who>{BILL_TO_LABEL[row.billTo]}</Who>
                    <Who>{row.track}</Who>
                    <Amount value={row.toInvoice} />
                    <Amount value={row.invoiced} />
                    <Amount value={row.paidThisMonth} />
                  </tr>
                )
              )}
            </tbody>
          </SummaryTable>
        </SummaryTables>
      )}
      <HubTable
        columns={columns}
        data={visible}
        loading={receivables.loading}
        emptyText="No receivables"
        emptyFallback={
          receivables.error && <LoadFailed what="receivables" error={receivables.error} onRetry={receivables.reload} />
        }
      />
      {!receivables.error && (
        <QueueFooter
          shown={visible.length}
          summary={
            <>
              <Count value={visible.length} /> of <Count value={receivables.receivables.length} /> receivables
            </>
          }
        />
      )}
    </Box>
  );
}
